import { useI18n } from "#i18n";
import { useHead, useRequestURL, useSiteConfig } from "#imports";
import { computed, ref } from "vue";

export default function usePageMeta() {
  const pageTitle = ref("");
  const pageDescription = ref("");
  const pageImage = ref("");
  const pageKeywords = ref("");

  const { locale, locales } = useI18n();
  // Build the canonical from the configured site URL (sourced from
  // NUXT_FRONTEND_URL via `site.url` in nuxt.config), not the request origin:
  // during prerendering `useRequestURL()` resolves to the internal Nitro
  // localhost server, which would otherwise bake `http://localhost/...` into
  // every prerendered page's canonical/og:url. `useSiteConfig()` is exposed to
  // the client, so the origin stays correct through hydration too.
  const requestUrl = useRequestURL();
  const siteConfig = useSiteConfig();
  // Canonical URLs never carry a trailing slash (except the root).
  const canonicalUrl = computed(
    () => `${siteConfig.url}${requestUrl.pathname.replace(/\/+$/, "") || "/"}`,
  );

  const ogLocaleMap = Object.fromEntries(
    locales.value.map((l) => {
      const code = typeof l === "string" ? l : l.code;
      const language = typeof l === "string" ? l : (l.language ?? l.code);
      return [code, language.replace("-", "_")];
    }),
  );

  useHead({
    title: computed(() => pageTitle.value),
    meta: computed(() => [
      // Only register non-empty values so site-wide defaults (layout) are
      // not overridden by empty page-level refs via unhead dedupe.
      ...(pageDescription.value
        ? [{ name: "description", content: pageDescription.value }]
        : []),
      ...(pageKeywords.value
        ? [{ name: "keywords", content: pageKeywords.value }]
        : []),
      // Open Graph
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: siteConfig.name ?? "" },
      ...(pageTitle.value
        ? [{ property: "og:title", content: pageTitle.value }]
        : []),
      ...(pageDescription.value
        ? [{ property: "og:description", content: pageDescription.value }]
        : []),
      { property: "og:url", content: canonicalUrl.value },
      { property: "og:locale", content: ogLocaleMap[locale.value] ?? "de_CH" },
      ...(pageImage.value
        ? [{ property: "og:image", content: pageImage.value }]
        : []),
      // Twitter Card
      { name: "twitter:card", content: "summary_large_image" },
      ...(pageTitle.value
        ? [{ name: "twitter:title", content: pageTitle.value }]
        : []),
      ...(pageDescription.value
        ? [{ name: "twitter:description", content: pageDescription.value }]
        : []),
      ...(pageImage.value
        ? [{ name: "twitter:image", content: pageImage.value }]
        : []),
    ]),
    link: computed(() => [{ rel: "canonical", href: canonicalUrl.value }]),
  });

  return { pageTitle, pageDescription, pageImage, pageKeywords };
}
